import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Users, Eye, Trash2, Search, Filter, Check, X, Crown, DollarSign, Upload,
  Star, Clock, Loader2, Shield, ExternalLink, Award, AlertTriangle, Handshake, ArrowUpRight, Pencil
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { API, PLAN_LABELS } from './adminConstants';
import { IconLegend } from '@/components/IconLegend';

const AFFILIATE_LEGEND = [
  { icon: <Pencil className="w-3.5 h-3.5" />, cls: 'bg-blue-50 text-blue-600', label: 'Corrigir dados (nome, e-mail, WhatsApp, PIX)' },
  { icon: <Check className="w-3.5 h-3.5" />, cls: 'bg-emerald-50 text-emerald-600', label: 'Aprovar cadastro pendente' },
  { icon: <X className="w-3.5 h-3.5" />, cls: 'bg-red-50 text-red-500', label: 'Rejeitar cadastro' },
  { icon: <Eye className="w-3.5 h-3.5" />, cls: 'bg-purple-50 text-purple-600', label: 'Entrar no painel como o afiliado' },
  { icon: <Trash2 className="w-3.5 h-3.5" />, cls: 'bg-red-50 text-red-500', label: 'Excluir / arquivar afiliado' },
  { icon: <Crown className="w-3.5 h-3.5" />, cls: 'bg-[#C5A059]/20 text-[#C5A059]', label: 'Selo do tipo: toque no selo Padrão/Master para promover ou rebaixar' },
  { icon: <DollarSign className="w-3.5 h-3.5" />, cls: 'bg-blue-50 text-blue-600', label: 'Bônus de rede do Master (toque para editar)' },
];

const AdminAffiliatesTab = () => {
  const { getToken } = useAuth();
  const [affiliates, setAffiliates] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [vipRequests, setVipRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoApprove, setAutoApprove] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [commissionFilter, setCommissionFilter] = useState('all');
  const [uploadingReceipt, setUploadingReceipt] = useState(null);
  const [payingCommission, setPayingCommission] = useState(null);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [editBonusValue, setEditBonusValue] = useState('');
  const [editAffInfo, setEditAffInfo] = useState(null);
  const [editAffData, setEditAffData] = useState({ name: '', email: '', whatsapp: '', pix_key: '' });
  const [savingAffInfo, setSavingAffInfo] = useState(false);

  const openEditAffInfo = (a) => {
    setEditAffInfo(a);
    setEditAffData({ name: a.name || '', email: a.email || '', whatsapp: a.whatsapp || '', pix_key: a.pix_key || '' });
  };

  const saveAffInfo = async () => {
    if (!editAffInfo) return;
    setSavingAffInfo(true);
    try {
      const token = getToken();
      await axios.put(`${API}/admin/affiliates/${editAffInfo.id}/info`, editAffData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Afiliado atualizado!');
      setEditAffInfo(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSavingAffInfo(false); }
  };
  const [savingType, setSavingType] = useState(null);
  const [partners, setPartners] = useState([]);
  const [linkingPartner, setLinkingPartner] = useState(null);
  const [totalInDb, setTotalInDb] = useState(0);
  const [totalArchived, setTotalArchived] = useState(0);
  const [archivedList, setArchivedList] = useState([]);
  const [showArchivedModal, setShowArchivedModal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const h = { Authorization: `Bearer ${token}` };
      const [affRes, commRes, settingsRes, vipRes, partnersRes] = await Promise.all([
        axios.get(`${API}/admin/affiliates`, { headers: h }),
        axios.get(`${API}/admin/affiliate-commissions`, { headers: h }),
        axios.get(`${API}/admin/affiliate-settings`, { headers: h }),
        axios.get(`${API}/admin/vip-requests`, { headers: h }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/partners`, { headers: h }).catch(() => ({ data: [] }))
      ]);
      const affData = affRes.data;
      if (Array.isArray(affData)) {
        setAffiliates(affData);
        setTotalInDb(affData.length);
        setTotalArchived(0);
      } else {
        setAffiliates(affData.affiliates || []);
        setTotalInDb(affData.total_in_db || 0);
        setTotalArchived(affData.total_archived || 0);
      }
      setCommissions(Array.isArray(commRes.data) ? commRes.data : commRes.data.commissions || []);
      setAutoApprove(settingsRes.data?.auto_approve ?? false);
      setVipRequests(Array.isArray(vipRes.data) ? vipRes.data : []);
      const pData = Array.isArray(partnersRes.data) ? partnersRes.data : [];
      setPartners(pData.filter(p => p.status === 'active'));
    } catch { toast.error('Erro ao carregar afiliados'); }
    finally { setLoading(false); }
  }, [getToken]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const approveAffiliate = async (id) => {
    try {
      const token = getToken();
      await axios.put(`${API}/admin/affiliates/${id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Afiliado aprovado!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const rejectAffiliate = async (id) => {
    try {
      const token = getToken();
      await axios.put(`${API}/admin/affiliates/${id}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Afiliado rejeitado');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const deleteAffiliate = async (id) => {
    try {
      const token = getToken();
      await axios.delete(`${API}/admin/affiliates/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Afiliado arquivado');
      setDeleteConfirm(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const toggleAutoApprove = async () => {
    try {
      const token = getToken();
      await axios.put(`${API}/admin/affiliate-settings`, { auto_approve: !autoApprove }, { headers: { Authorization: `Bearer ${token}` } });
      setAutoApprove(!autoApprove);
      toast.success(`Auto-aprovação ${!autoApprove ? 'ativada' : 'desativada'}`);
    } catch { toast.error('Erro'); }
  };

  const handleImpersonate = async (affiliateId) => {
    try {
      const token = getToken();
      const res = await axios.post(`${API}/admin/impersonate`, { type: 'affiliate', id: affiliateId }, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem('affiliate_token', res.data.token);
      window.open(res.data.redirect || '/afiliados/painel', '_blank');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao personificar'); }
  };

  const approveVipRequest = async (requestId) => {
    try {
      const token = getToken();
      await axios.put(`${API}/admin/vip-requests/${requestId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('VIP aprovado!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const rejectVipRequest = async (requestId) => {
    try {
      const token = getToken();
      await axios.put(`${API}/admin/vip-requests/${requestId}/reject`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('VIP rejeitado');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const handleUploadReceipt = async (commId, file) => {
    setUploadingReceipt(commId);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      const receiptUrl = uploadRes.data.url;
      await axios.put(`${API}/admin/affiliate-commissions/${commId}/pay`, { receipt_url: receiptUrl }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Comprovante enviado e comissão marcada como paga!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao enviar comprovante'); }
    finally { setUploadingReceipt(null); }
  };

  const payCommissionWithoutReceipt = async (commId) => {
    setPayingCommission(commId);
    try {
      const token = getToken();
      await axios.put(`${API}/admin/affiliate-commissions/${commId}/pay`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Comissão marcada como paga');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    finally { setPayingCommission(null); }
  };

  const toggleAffiliateType = async (aff) => {
    const newType = aff.affiliate_type === 'master' ? 'padrão' : 'master';
    const isPromoting = newType === 'master';
    const label = isPromoting ? 'Master' : 'Padrão';
    const action = isPromoting ? '🚀 PROMOVER' : '⬇️ REBAIXAR';
    const benefit = isPromoting
      ? '\n\n✅ Como Master ele:\n• Pode ter Afiliados debaixo dele\n• Ganha de R$ 50 a R$ 100 de bônus por cada venda da rede (conforme o plano)\n• Pode visualizar e gerenciar a equipe\n\nDeseja prosseguir?'
      : '\n\n⚠️ Como Padrão ele:\n• Perde a possibilidade de ter equipe\n• Não receberá mais o bônus por venda da rede\n\nDeseja prosseguir?';
    if (!window.confirm(`${action}\n\n${aff.name} → ${label}${benefit}`)) return;
    setSavingType(aff.id);
    try {
      const token = getToken();
      await axios.put(`${API}/admin/affiliates/${aff.id}/type`, {
        affiliate_type: newType
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${aff.name} agora é ${label}!`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao alterar tipo'); }
    finally { setSavingType(null); }
  };

  const saveMasterBonus = async (affId) => {
    const val = parseFloat(editBonusValue);
    if (isNaN(val) || val < 0) { toast.error('Valor invalido'); return; }
    setSavingType(affId);
    try {
      const token = getToken();
      await axios.put(`${API}/admin/affiliates/${affId}/type`, {
        affiliate_type: 'master',
        master_bonus_amount: val
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Bonus Master atualizado para R$ ${val.toFixed(2)}`);
      setEditingAffiliate(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao salvar bonus'); }
    finally { setSavingType(null); }
  };

  const linkPartner = async (affId, partnerId) => {
    setLinkingPartner(affId);
    try {
      const token = getToken();
      await axios.put(`${API}/admin/affiliates/${affId}/link-partner`, {
        partner_id: partnerId || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(partnerId ? 'Afiliado vinculado ao parceiro!' : 'Afiliado desvinculado do parceiro');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao vincular'); }
    finally { setLinkingPartner(null); }
  };

  const masterAffiliates = affiliates.filter(a => a.affiliate_type === 'master' && a.status === 'approved');

  const migrateMaster = async (affId, newMasterId) => {
    try {
      const token = getToken();
      await axios.put(`${API}/admin/affiliates/${affId}/migrate-master`, {
        master_id: newMasterId || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newMasterId ? 'Master alterado!' : 'Desvinculado do master');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao migrar'); }
  };



  const filteredAffiliates = affiliates.filter(a => {
    if (typeFilter !== 'all') {
      // Normaliza para comparacao (remove acentos para casar 'padrao' com 'padrão')
      const affType = (a.affiliate_type || 'padrao').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const filterType = typeFilter.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      if (affType !== filterType) return false;
    }
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const haystack = [
        a.name, a.email, a.whatsapp, a.cpf_cnpj, a.pix_key,
        a.slug, a.referral_code, a.linked_partner_name
      ].filter(Boolean).join(' ').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return haystack.includes(term);
    }
    return true;
  });

  const filteredCommissions = commissions.filter(c => {
    const st = c.installment_1?.status;
    if (commissionFilter === 'pending') return st === 'pending';
    if (commissionFilter === 'awaiting_client_payment') return st === 'awaiting_client_payment';
    if (commissionFilter === 'approved') return st === 'approved';
    if (commissionFilter === 'approved_no_balance') return ['approved_no_balance', 'approved_transfer_error', 'approved_no_pix'].includes(st);
    if (commissionFilter === 'paid') return st === 'paid';
    return true;
  });

  const pendingVip = vipRequests.filter(v => v.status === 'pending');

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin w-10 h-10 border-4 border-[#C5A059] border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="afiliados-section">
      {/* Settings Card */}
      <Card className="border-0 shadow-sm border-l-4 border-l-purple-500">
        <CardContent className="p-4 flex items-center justify-between">
          <div><p className="font-semibold text-sm text-slate-900">Auto-Aprovação de Afiliados</p><p className="text-xs text-slate-500">Novos afiliados serao aprovados automaticamente</p></div>
          <button onClick={toggleAutoApprove} className={`relative w-12 h-6 rounded-full transition-colors ${autoApprove ? 'bg-emerald-500' : 'bg-slate-300'}`} data-testid="auto-approve-toggle"><span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${autoApprove ? 'right-1' : 'left-1'}`} /></button>
        </CardContent>
      </Card>

      {/* VIP Requests */}
      {pendingVip.length > 0 && (
        <Card className="border-0 shadow-sm border-l-4 border-l-[#C5A059]">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Crown className="w-5 h-5 text-[#C5A059]" />Solicitacoes VIP Pendentes ({pendingVip.length})</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {pendingVip.map(v => (
              <div key={v.id} className="flex items-center justify-between p-3 bg-[#C5A059]/5 rounded-xl border border-[#C5A059]/20" data-testid={`vip-request-${v.id}`}>
                <div>
                  <p className="font-semibold text-sm text-slate-900">{v.affiliate_name} solicita VIP para <strong>{v.company_name}</strong></p>
                  <p className="text-xs text-slate-500">{v.requested_months} meses | {v.created_at?.slice(0, 10)}</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => approveVipRequest(v.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8 px-3" data-testid={`approve-vip-${v.id}`}><Check className="w-3 h-3 mr-1" />Aprovar</Button>
                  <Button onClick={() => rejectVipRequest(v.id)} variant="outline" className="text-xs h-8 px-3 text-red-600 border-red-200 hover:bg-red-50" data-testid={`reject-vip-${v.id}`}><X className="w-3 h-3 mr-1" />Rejeitar</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Health Alerts Banner */}
      {(() => {
        const unhealthy = affiliates.filter(a => a.health_issues?.length > 0);
        if (unhealthy.length === 0) return null;
        return (
          <Card className="border-0 shadow-sm border-l-4 border-l-amber-500 bg-amber-50/50" data-testid="health-alerts-banner">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <p className="font-semibold text-sm text-amber-800">{unhealthy.length} cadastro(s) incompleto(s)</p>
              </div>
              <div className="space-y-1.5">
                {unhealthy.slice(0, 5).map(a => (
                  <div key={a.id} className="flex items-start gap-2 text-xs" data-testid={`health-issue-${a.id}`}>
                    <span className="font-semibold text-amber-700 shrink-0">{a.name}:</span>
                    <span className="text-amber-600">{a.health_issues.join(' | ')}</span>
                  </div>
                ))}
                {unhealthy.length > 5 && <p className="text-[10px] text-amber-500">e mais {unhealthy.length - 5}...</p>}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Affiliates Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-600" />
              Afiliados <span className="text-sm font-normal text-slate-500">({filteredAffiliates.length} exibidos · {totalInDb} no banco · {totalArchived} arquivados)</span>
            </CardTitle>
            {totalArchived > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const token = getToken();
                    const r = await axios.get(`${API}/admin/affiliates/archived`, { headers: { Authorization: `Bearer ${token}` } });
                    setArchivedList(r.data || []);
                    setShowArchivedModal(true);
                  } catch { toast.error('Erro ao carregar arquivados'); }
                }}
                className="text-xs gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="open-archived-affiliates"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Ver arquivados ({totalArchived})
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-3 mt-3">
            <div className="relative flex-1 min-w-[200px]"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" /><input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar por nome, email, WhatsApp, CPF, PIX, código..." className="w-full pl-10 pr-3 h-9 border border-slate-300 rounded-lg text-sm" data-testid="search-affiliates" /></div>
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="filter-affiliate-type"><option value="all">Todos os Tipos</option><option value="master">Master</option><option value="padrao">Padrão</option></select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="h-9 px-3 border border-slate-300 rounded-lg text-sm bg-white" data-testid="filter-affiliate-status"><option value="all">Todos os Status</option><option value="pending">Pendente</option><option value="approved">Aprovado</option><option value="rejected">Rejeitado</option></select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="px-4 pt-3 pb-1"><IconLegend items={AFFILIATE_LEGEND} /></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="affiliates-table">
              <thead><tr className="bg-slate-50 border-b text-slate-500 text-xs"><th className="text-left px-4 py-3">Afiliado</th><th className="text-center px-3 py-3">Tipo</th><th className="text-center px-3 py-3">Parceiro</th><th className="text-center px-3 py-3">Origem</th><th className="text-center px-3 py-3">Status</th><th className="text-center px-3 py-3">Conv.</th><th className="text-center px-3 py-3">Comissoes</th><th className="text-center px-3 py-3">Acoes</th></tr></thead>
              <tbody>
                {filteredAffiliates.map(a => (
                  <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`affiliate-row-${a.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{a.name}</p>
                      <p className="text-xs text-slate-400">{a.email} | PIX: {a.pix_key || '-'}</p>
                      {a.master_name && <p className="text-[10px] text-[#C5A059]">Vinculado a: {a.master_name}</p>}
                      {a.affiliate_type === 'padrão' && (
                        <div className="mt-0.5 flex items-center gap-1">
                          <span className="text-[9px] text-slate-400">Master:</span>
                          <select
                            value={a.master_id || ''}
                            onChange={e => migrateMaster(a.id, e.target.value)}
                            className="h-5 text-[9px] border border-slate-200 rounded px-1 bg-white text-slate-500"
                            data-testid={`migrate-master-${a.id}`}
                          >
                            <option value="">Sem master</option>
                            {masterAffiliates.filter(m => m.id !== a.id).map(m => (
                              <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {a.health_issues?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1" data-testid={`health-badges-${a.id}`}>
                          {a.health_issues.map(issue => (
                            <span key={issue} className="text-[9px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200">{issue}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="text-center px-3 py-3">
                      <button
                        onClick={() => toggleAffiliateType(a)}
                        disabled={savingType === a.id}
                        className={`text-xs font-semibold px-2.5 py-1.5 rounded-full transition-all cursor-pointer hover:ring-2 hover:ring-offset-1 inline-flex items-center gap-1 ${
                          a.affiliate_type === 'master'
                            ? 'bg-[#C5A059]/20 text-[#C5A059] hover:ring-[#C5A059]/40'
                            : 'bg-slate-100 text-slate-600 hover:ring-slate-300'
                        }`}
                        title={a.affiliate_type === 'master' ? 'Clique para REBAIXAR para Padrão' : 'Clique para PROMOVER para Master'}
                        data-testid={`toggle-type-${a.id}`}
                      >
                        {savingType === a.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            {a.affiliate_type === 'master' ? '👑 Master' : 'Padrão'}
                            <ArrowUpRight className="w-3 h-3 opacity-60" />
                          </>
                        )}
                      </button>
                      {a.affiliate_type === 'master' && (
                        <div className="mt-1.5">
                          {editingAffiliate === a.id ? (
                            <div className="flex items-center gap-1 justify-center" data-testid={`edit-bonus-form-${a.id}`}>
                              <span className="text-[10px] text-slate-400">R$</span>
                              <input
                                type="number"
                                value={editBonusValue}
                                onChange={e => setEditBonusValue(e.target.value)}
                                className="w-16 h-6 text-[11px] text-center border border-[#C5A059] rounded px-1"
                                min="0"
                                step="0.01"
                                data-testid={`bonus-input-${a.id}`}
                              />
                              <button onClick={() => saveMasterBonus(a.id)} className="p-0.5 text-emerald-600 hover:text-emerald-800" data-testid={`save-bonus-${a.id}`}><Check className="w-3.5 h-3.5" /></button>
                              <button onClick={() => setEditingAffiliate(null)} className="p-0.5 text-slate-400 hover:text-red-500"><X className="w-3.5 h-3.5" /></button>
                            </div>
                          ) : (
                            <button
                              onClick={() => { setEditingAffiliate(a.id); setEditBonusValue(String(a.master_bonus_amount || 50)); }}
                              className="text-[10px] text-[#C5A059] hover:underline cursor-pointer"
                              title="Clique para editar o bonus por venda da equipe"
                              data-testid={`edit-bonus-btn-${a.id}`}
                            >
                              Bonus: {a.master_bonus_amount && a.master_bonus_amount !== 50 ? `R$ ${a.master_bonus_amount.toFixed(2)}/venda` : 'R$ 50–100/venda (por plano)'}
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="text-center px-3 py-3" data-testid={`partner-link-cell-${a.id}`}>
                      {linkingPartner === a.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin inline text-slate-400" />
                      ) : a.linked_partner_name ? (
                        <div className="flex flex-col items-center gap-0.5">
                          <span className="text-[10px] font-semibold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Handshake className="w-3 h-3" />{a.linked_partner_name}
                          </span>
                          <button onClick={() => linkPartner(a.id, null)} className="text-[9px] text-red-400 hover:text-red-600 hover:underline" data-testid={`unlink-partner-${a.id}`}>
                            desvincular
                          </button>
                        </div>
                      ) : partners.length > 0 ? (
                        <select
                          onChange={e => { if (e.target.value) linkPartner(a.id, e.target.value); }}
                          defaultValue=""
                          className="h-7 text-[10px] border border-slate-200 rounded px-1 bg-white text-slate-500 max-w-[100px]"
                          data-testid={`link-partner-select-${a.id}`}
                        >
                          <option value="">Vincular...</option>
                          {partners.map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-[10px] text-slate-300">-</span>
                      )}
                    </td>
                    <td className="text-center px-3 py-3" data-testid={`origin-${a.id}`}>
                      {(() => {
                        const icon = a.origin_icon || 'direct';
                        const label = a.origin_label || 'Direto';
                        const styles = {
                          partner: 'bg-cyan-50 text-cyan-700 border-cyan-200',
                          ads: 'bg-purple-50 text-purple-700 border-purple-200',
                          referrer: 'bg-amber-50 text-amber-700 border-amber-200',
                          direct: 'bg-slate-50 text-slate-500 border-slate-200',
                        };
                        const emoji = { partner: '🤝', ads: '📢', referrer: '🔗', direct: '✋' }[icon];
                        return (
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border ${styles[icon]}`}
                            title={JSON.stringify(a.attribution || {}, null, 2)}
                          >
                            <span>{emoji}</span>
                            <span className="truncate max-w-[120px]">{label}</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="text-center px-3 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${a.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : a.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{a.status === 'approved' ? 'Aprovado' : a.status === 'pending' ? 'Pendente' : 'Rejeitado'}</span></td>
                    <td className="text-center px-3 py-3 font-bold text-slate-700">{a.referral_count || 0}</td>
                    <td className="text-center px-3 py-3 text-emerald-600 font-medium">R$ {(a.total_earned || 0).toFixed(2)}</td>
                    <td className="text-center px-3 py-3">
                      <div className="flex gap-1 justify-center">
                        {a.status === 'pending' && <>
                          <button onClick={() => approveAffiliate(a.id)} className="p-1.5 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100" title="Aprovar" data-testid={`approve-aff-${a.id}`}><Check className="w-3.5 h-3.5" /></button>
                          <button onClick={() => rejectAffiliate(a.id)} className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100" title="Rejeitar" data-testid={`reject-aff-${a.id}`}><X className="w-3.5 h-3.5" /></button>
                        </>}
                        <button onClick={() => openEditAffInfo(a)} className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100" title="Corrigir dados" data-testid={`edit-aff-${a.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handleImpersonate(a.id)} className="p-1.5 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100" title="Personificar" data-testid={`impersonate-aff-${a.id}`}><Eye className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setDeleteConfirm(a)} className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100" title="Excluir" data-testid={`delete-aff-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredAffiliates.length === 0 && <div className="py-8 text-center text-slate-400 text-sm">Nenhum afiliado encontrado.</div>}
        </CardContent>
      </Card>

      {/* Gestor de Comissoes */}
      <Card className="border-0 shadow-sm border-l-4 border-l-emerald-500">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-600" />Gestor de Comissoes</CardTitle>
          <CardDescription>Aprove comissoes para transferencia automática via Pix</CardDescription>
          <div className="flex gap-2 mt-2">
            <select value={commissionFilter} onChange={e => setCommissionFilter(e.target.value)} className="h-8 px-3 border border-slate-300 rounded-lg text-xs bg-white" data-testid="filter-commission-status">
              <option value="all">Todas</option>
              <option value="pending">A pagar</option>
              <option value="awaiting_client_payment">Aguardando pagamento do cliente</option>
              <option value="approved">Aprovadas</option>
              <option value="approved_no_balance">Sem Saldo</option>
              <option value="paid">Pagas</option>
            </select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="commissions-table">
              <thead><tr className="bg-slate-50 border-b text-slate-500 text-xs"><th className="text-left px-4 py-3">Afiliado</th><th className="text-left px-3 py-3">Empresa</th><th className="text-center px-3 py-3">Plano</th><th className="text-center px-3 py-3">Valor</th><th className="text-center px-3 py-3">Status</th><th className="text-center px-3 py-3">Acoes</th></tr></thead>
              <tbody>
                {filteredCommissions.map(c => {
                  const inst = c.installment_1 || {};
                  const statusMap = {
                    'awaiting_client_payment': { label: 'Aguardando pagamento do cliente', bg: 'bg-slate-100 text-slate-600' },
                    'pending': { label: 'A pagar', bg: 'bg-amber-100 text-amber-700' },
                    'approved': { label: 'Aprovado', bg: 'bg-blue-100 text-blue-700' },
                    'approved_no_balance': { label: 'Sem Saldo', bg: 'bg-red-100 text-red-700' },
                    'approved_no_pix': { label: 'Sem Pix', bg: 'bg-orange-100 text-orange-700' },
                    'approved_transfer_error': { label: 'Erro Pix', bg: 'bg-red-100 text-red-700' },
                    'paid': { label: 'Pago', bg: 'bg-emerald-100 text-emerald-700' },
                  };
                  const st = statusMap[inst.status] || { label: inst.status, bg: 'bg-slate-100 text-slate-600' };
                  return (
                    <tr key={c.id} className={`border-b border-slate-100 ${inst.status === 'paid' ? 'bg-emerald-50/30' : ''}`} data-testid={`commission-row-${c.id}`}>
                      <td className="px-4 py-3"><p className="font-semibold text-slate-900">{c.affiliate_name}</p><p className="text-[10px] text-slate-400">PIX: {c.affiliate_pix || 'Não cadastrado'}</p></td>
                      <td className="px-3 py-3"><p className="text-slate-800">{c.company_name}</p>{c.is_master_bonus && <p className="text-[10px] text-[#C5A059] font-semibold">Bonus Master</p>}</td>
                      <td className="text-center px-3 py-3 text-xs">{c.plan_name || '-'}</td>
                      <td className="text-center px-3 py-3 font-bold text-emerald-600">R$ {(inst.amount || 0).toFixed(2)}</td>
                      <td className="text-center px-3 py-3">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${st.bg}`}>{st.label}</span>
                        {inst.status === 'paid' && inst.payment_method === 'pix_auto' && (
                          <p className="text-[9px] text-emerald-500 mt-0.5">Pix Automático</p>
                        )}
                      </td>
                      <td className="text-center px-3 py-3">
                        {inst.status === 'pending' && (
                          <div className="flex items-center justify-center gap-1.5 flex-wrap">
                            <button onClick={async () => {
                              if (!window.confirm(`Aprovar R$ ${(inst.amount || 0).toFixed(2)} para ${c.affiliate_name}?`)) return;
                              setPayingCommission(c.id);
                              try {
                                const token = getToken();
                                const res = await axios.put(`${API}/admin/commissions/${c.id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                const data = res.data;
                                if (data.status === 'paid') toast.success(data.message);
                                else if (data.status === 'approved_no_balance') toast.error(data.message);
                                else if (data.status === 'approved_no_pix') toast.error(data.message);
                                else toast.info(data.message || 'Aprovado');
                                fetchData();
                              } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao aprovar'); }
                              finally { setPayingCommission(null); }
                            }} disabled={payingCommission === c.id}
                              className="px-3 py-1.5 rounded-lg bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] text-xs font-bold transition-colors"
                              data-testid={`approve-commission-${c.id}`}>
                              {payingCommission === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Aprovar Pix'}
                            </button>
                            <button onClick={async () => {
                              const reason = window.prompt(`Confirmar pagamento MANUAL de R$ ${(inst.amount || 0).toFixed(2)} para ${c.affiliate_name}?\n\nDescreva como foi pago (ex: "Pix manual via Asaas", "Dinheiro", "TED"):`, 'Pix manual via Asaas');
                              if (reason === null) return;
                              setPayingCommission(c.id);
                              try {
                                const token = getToken();
                                const res = await axios.post(`${API}/admin/commissions/${c.id}/mark-paid`, { reason: reason || 'Pagamento manual' }, { headers: { Authorization: `Bearer ${token}` } });
                                toast.success(res.data.message || 'Marcada como paga');
                                fetchData();
                              } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao marcar'); }
                              finally { setPayingCommission(null); }
                            }} disabled={payingCommission === c.id}
                              className="px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200 transition-colors"
                              data-testid={`mark-paid-commission-${c.id}`}>
                              Marcar Paga
                            </button>
                          </div>
                        )}
                        {(inst.status === 'approved_no_balance' || inst.status === 'approved_transfer_error' || inst.status === 'approved_no_pix') && (
                          <div className="flex items-center justify-center gap-1.5 flex-wrap">
                            {inst.status !== 'approved_no_pix' && (
                              <button onClick={async () => {
                                setPayingCommission(c.id);
                                try {
                                  const token = getToken();
                                  const res = await axios.put(`${API}/admin/commissions/${c.id}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
                                  const data = res.data;
                                  if (data.status === 'paid') toast.success(data.message);
                                  else toast.error(data.message);
                                  fetchData();
                                } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
                                finally { setPayingCommission(null); }
                              }} disabled={payingCommission === c.id}
                                className="px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-xs font-semibold border border-red-200 transition-colors"
                                data-testid={`retry-commission-${c.id}`}>
                                {payingCommission === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Tentar Novamente'}
                              </button>
                            )}
                            <button onClick={async () => {
                              const reason = window.prompt(`Confirmar pagamento MANUAL de R$ ${(inst.amount || 0).toFixed(2)} para ${c.affiliate_name}?\n\nDescreva como foi pago:`, 'Pix manual via Asaas');
                              if (reason === null) return;
                              setPayingCommission(c.id);
                              try {
                                const token = getToken();
                                const res = await axios.post(`${API}/admin/commissions/${c.id}/mark-paid`, { reason: reason || 'Pagamento manual' }, { headers: { Authorization: `Bearer ${token}` } });
                                toast.success(res.data.message || 'Marcada como paga');
                                fetchData();
                              } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao marcar'); }
                              finally { setPayingCommission(null); }
                            }} disabled={payingCommission === c.id}
                              className="px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-semibold border border-emerald-200 transition-colors"
                              data-testid={`mark-paid-commission-${c.id}`}>
                              Marcar Paga
                            </button>
                          </div>
                        )}
                        {inst.status === 'approved_no_pix' && (
                          <p className="text-[10px] text-orange-600 mt-1">Peca ao afiliado cadastrar Pix</p>
                        )}
                        {inst.status === 'approved' && (
                          <span className="text-[10px] text-blue-600">Processando...</span>
                        )}
                        {inst.status === 'paid' && (
                          <span className="text-[10px] text-emerald-600">
                            {inst.payment_method === 'manual'
                              ? `Manual: ${inst.payment_reason || 'Pago externo'}`
                              : (inst.transfer_id ? `ID: ${inst.transfer_id.slice(0, 12)}...` : 'Automático')}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredCommissions.length === 0 && <div className="py-8 text-center text-slate-400 text-sm">Nenhuma comissão encontrada.</div>}
        </CardContent>
      </Card>

      {editAffInfo && (
        <div className="fixed inset-0 z-[99] bg-black/60 flex items-center justify-center p-4" data-testid="edit-affiliate-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-[#0A2647] mb-4">Corrigir dados: {editAffInfo.name}</h3>
            <div className="space-y-3">
              <div><label className="text-xs font-medium text-slate-600 mb-1 block">Nome</label><input value={editAffData.name} onChange={e => setEditAffData({...editAffData, name: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-aff-name" /></div>
              <div><label className="text-xs font-medium text-slate-600 mb-1 block">E-mail</label><input value={editAffData.email} onChange={e => setEditAffData({...editAffData, email: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-aff-email" /></div>
              <div><label className="text-xs font-medium text-slate-600 mb-1 block">WhatsApp</label><input value={editAffData.whatsapp} onChange={e => setEditAffData({...editAffData, whatsapp: e.target.value})} placeholder="DDD + número" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-aff-whatsapp" /></div>
              <div><label className="text-xs font-medium text-slate-600 mb-1 block">Chave PIX</label><input value={editAffData.pix_key} onChange={e => setEditAffData({...editAffData, pix_key: e.target.value})} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="edit-aff-pix" /></div>
            </div>
            <div className="flex gap-3 mt-5">
              <Button onClick={saveAffInfo} disabled={savingAffInfo} className="flex-1 bg-[#C5A059] hover:bg-[#b8933f] text-[#0A2647] font-semibold" data-testid="save-aff-info">{savingAffInfo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}</Button>
              <Button variant="outline" onClick={() => setEditAffInfo(null)} className="flex-1">Cancelar</Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[99] bg-black/60 flex items-center justify-center p-4" data-testid="delete-confirm-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
            <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 className="w-7 h-7 text-amber-600" /></div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Arquivar Afiliado?</h3>
            <p className="text-sm text-slate-500 mb-2">
              <strong>{deleteConfirm.name}</strong> será arquivado (oculto da lista).
            </p>
            <p className="text-xs text-emerald-600 mb-6">
              ✓ Os dados ficam preservados<br/>
              ✓ Você pode restaurar a qualquer momento em "Ver arquivados"
            </p>
            <div className="flex gap-3">
              <Button onClick={() => deleteAffiliate(deleteConfirm.id)} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-delete-btn">Arquivar</Button>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="flex-1">Cancelar</Button>
            </div>
          </div>
        </div>
      )}

      {/* ARCHIVED AFFILIATES MODAL */}
      {showArchivedModal && (
        <div className="fixed inset-0 z-[99] bg-black/60 flex items-center justify-center p-4" data-testid="archived-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-1">Afiliados Arquivados</p>
                <h3 className="text-lg font-bold text-slate-900">{archivedList.length} afiliado(s) arquivado(s)</h3>
                <p className="text-xs text-slate-500 mt-0.5">Os dados estão preservados — você pode restaurar a qualquer momento.</p>
              </div>
              <button onClick={() => setShowArchivedModal(false)} className="p-1 hover:bg-slate-100 rounded">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>

            {archivedList.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">Nenhum afiliado arquivado.</p>
            ) : (
              <div className="space-y-2">
                {archivedList.map(a => (
                  <div key={a.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{a.name}</p>
                      <p className="text-xs text-slate-500 truncate">{a.email}</p>
                      {a.archived_at && (
                        <p className="text-[10px] text-amber-600 mt-0.5">
                          Arquivado em {new Date(a.archived_at).toLocaleString('pt-BR')}
                        </p>
                      )}
                    </div>
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          const token = getToken();
                          await axios.post(`${API}/admin/affiliates/${a.id}/unarchive`, {}, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success('Afiliado restaurado!');
                          setArchivedList(prev => prev.filter(x => x.id !== a.id));
                          fetchData();
                        } catch { toast.error('Erro ao restaurar'); }
                      }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1"
                      data-testid={`restore-affiliate-${a.id}`}
                    >
                      <Check className="w-3.5 h-3.5" /> Restaurar
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAffiliatesTab;
